import Task from "../../../../core/model2/task/task";
import Pin from "../../../../core/model2/pin/pin";
import ProjectSession from "../../../../core/model2/project/project-session";
import {PinType} from "../../../../core/model2/pin/@types";
import {EntityType} from "../../../../core/gd/const/item";
import {SearchResultItemType} from "./@type";
import {GroupedData} from "../../../../core/data-structures/group";
import CustomView from "../../../../core/model2/custom-view/custom-view";
import ProjectCollectionModel from "../../../../core/model2/project-collection/project-collection-model";
import {SpaceProto} from "../../../../core/model2/space/space-proto";
import {NavigationSectionType} from "../../../../core/model2/navigation/@const";
import asyncSessionLoader from "../../../../core/gd/data/async-session-loader";
import TasksCollection from "../../../../core/model2/task/collection";

export default class NavigationMenuSearch {

    public searchValue  : string
    public space        : SpaceProto

    public projects     : ProjectSession[]
    public tags         : ProjectSession[]
    public tasks        : Task[]
    public pinnedItems  : Pin[]
    public wsViews      : ProjectSession[]
    public pViews       : CustomView[]

    constructor(searchValue: string, space: SpaceProto) {

        this.searchValue    = searchValue
        this.space          = space

        this.projects       = []
        this.tags           = []
        this.tasks          = []
        this.pinnedItems    = []
        this.wsViews        = []
        this.pViews         = []
    }

    async search(parentProjectIds: ID[], pinnedItems: Pin[], wsViews: ProjectSession[], pViews: CustomView[] | ProjectCollectionModel[] = [], rootTags: ProjectSession[] = []) {

        const searchResult: GroupedData = []

        if(this.space.id !== NavigationSectionType.HOME){

            const loadMissingData = async (tasks: Task[])=> {
                const taskTypes     : ID[] = []
                const projectIds    : ID[] = []

                tasks?.map((task: Task)=>{
                    if (task) {
                        taskTypes.push(task.taskTypeId)
                        projectIds.push(task.projectId)
                    }})

                const missingData = gd.session.findMissing({
                    taskTypes   : _.uniq(taskTypes),
                    projects    : _.uniq(projectIds),
                })

                //load missing task types if needed
                if (_.keys(missingData).length > 0) {
                   await asyncSessionLoader(missingData)
                }
            }

            //Tasks
            try {
                const searchResponse = await gd.api.Search.quick(this.searchValue, parentProjectIds).promise()

                this.tasks = TasksCollection.initFromJson(searchResponse.result?.tasks).models

                await loadMissingData(this.tasks)

            } catch (e){
                console.error(e)
                this.tasks = []
            }
        }

        //Projects
        let allChildren: ProjectSession[] = []

        _.map(parentProjectIds, (projectId: ID)=>{
            allChildren = [...allChildren, ...gd.session.projects.getAllChildrenOptimized(projectId)]
        })

        this.projects = allChildren.filter((project: ProjectSession)=>{

            return  project.name.toLowerCase().includes(this.searchValue.toLowerCase()) &&
                    !project.isView &&
                    !project.isWorkspace;
                    // !project.isTag;

        })

        //Tags
        this.tags = rootTags.filter((project: ProjectSession)=>{
            return  project.name.toLowerCase().includes(this.searchValue.toLowerCase())
        })

        //Workspace views
        this.wsViews = wsViews.filter((project: ProjectSession)=>{
            return project.name.toLowerCase().includes(this.searchValue.toLowerCase())
        })

        //Projects views (Here Custom views and portfolio views)
        // @ts-ignore
        this.pViews = pViews.filter((view: CustomView | ProjectCollectionModel)=>{
            return view.name.toLowerCase().includes(this.searchValue.toLowerCase())
        })

        //Pinned items
        this.pinnedItems = pinnedItems.filter((pinnedItem)=>{

            switch (pinnedItem.getType()) {
                case PinType.CUSTOM_VIEW:
                case PinType.PROJECT: {
                    return pinnedItem.project?.name.toLowerCase().includes(this.searchValue.toLowerCase())
                }
                case PinType.TASK: {
                    return pinnedItem.task?.title.toLowerCase().includes(this.searchValue.toLowerCase())
                }
                case PinType.DOCUMENT: {
                    return pinnedItem.document?.name.toLowerCase().includes(this.searchValue.toLowerCase())
                }
                case PinType.USER: {
                    // @ts-ignore
                    const user = gd.session.users.get(pinnedItem.user?.userId)!
                    return  user.name.toLowerCase().includes(this.searchValue.toLowerCase())
                }
            }
        })

        if(!_.isEmpty(this.projects)){
            searchResult.push({
                label   : "Projects",
                items   : this.normalizeProjects()
            })
        }

        if(!_.isEmpty(this.tags)){
            searchResult.push({
                label   : "Tags",
                items   : this.normalizeTags()
            })
        }

        if(!_.isEmpty(this.tasks)){
            searchResult.push({
                label   : "Tasks",
                items   : this.normalizeTasks()
            })
        }

        if(!_.isEmpty(this.pinnedItems)){
            searchResult.push({
                label   : "Pinned Items",
                items   : this.normalizePinnedItems()
            })
        }

        if(!_.isEmpty(this.wsViews)){
            searchResult.push({
                label   : "Views",
                items   : this.normalizeWsViews()
            })
        }

        if(!_.isEmpty(this.pViews)){
            searchResult.push({
                label   : "Views",
                items   : this.normalizeProjectViews()
            })
        }

        return searchResult
    }

    normalizeProjects(): SearchResultItemType[] {
        return this.projects?.map((project: ProjectSession) => {
            return {
                id      : project.id,
                type    : EntityType.PROJECT,
                project : project
            }
        })
    }

    normalizeTags(): SearchResultItemType[] {
        return this.tags?.map((project: ProjectSession) => {
            return {
                id      : project.id,
                type    : EntityType.PROJECT,
                project : project
            }
        })
    }

    normalizeTasks(): SearchResultItemType[] {
        return this.tasks?.map((task: Task) => {
            return {
                id      : task.id,
                type    : EntityType.TASK,
                task    : task
            }
        })
    }

    normalizePinnedItems(): SearchResultItemType[] {
        return this.pinnedItems?.map((pinnedItem) => {
            return {
                id      : pinnedItem.project ? pinnedItem.project.id : pinnedItem.id,
                type    : EntityType.PIN,
                pin     : pinnedItem
            }
        })
    }

    normalizeWsViews(): SearchResultItemType[] {
        return this.wsViews?.map((view: ProjectSession) => {
            return {
                id      : view.id,
                type    : EntityType.PROJECT,
                project : view
            }
        })
    }

    normalizeProjectViews(): SearchResultItemType[] {

        return this.pViews?.map((view: CustomView | ProjectCollectionModel) => {
            if(view.entityType === EntityType.PORTFOLIO){
                return {
                    id      : view.id,
                    type    : EntityType.PORTFOLIO,
                    view    : view
                }
            }

            return {
                id      : view.id,
                type    : EntityType.CUSTOM_VIEW,
                view    : view
            }
        })
    }
}
